import { useState, memo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  InputAdornment,
  TextField,
} from '@mui/material';
import { ItemHead, ItemSeparator } from 'app/design/speedBot/EntityItem';
import FormattedMessage from 'app/design/uiComponents/FormattedMessage';
import { FieldData } from '../../Builder/Strategy/fields/types';
import StrategyBuilder from '../../Builder/Strategy/StrategyBuilder';
import { StrategyMode } from '../../Builder/Strategy/utils/strategy-data';
import { PositionDirectionData } from 'app/components/Bots/StrategyBuilder/util';
import CodeIcon from '@mui/icons-material/Code';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoIcon from '@mui/icons-material/Info';
import { FeatureFlag } from 'app/components/Common/FeatureFlag';
import { FEATURES } from 'types/Feature';

// const ItemMenu = ({
//   item,
//   handleItemClick,
// }: {
//   item: string | ReactElement;
//   handleItemClick: () => void;
// }) => {
//   const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
//   const open = Boolean(anchorEl);
//   const handleIconClick = (event: React.MouseEvent<HTMLElement>) => {
//     setAnchorEl(event.currentTarget);
//   };
//   const handleClose = () => {
//     setAnchorEl(null);
//   };
//   const handleClick = () => {
//     handleClose();
//     handleItemClick();
//   };
//   return (
//     <div>
//       <IconButton
//         aria-label="more"
//         id={`long-button`}
//         aria-controls={open ? `long-menu` : undefined}
//         aria-expanded={open ? 'true' : undefined}
//         aria-haspopup="true"
//         onClick={handleIconClick}
//       >
//         <MoreVertIcon />
//       </IconButton>
//       <Menu
//         id={`long-menu`}
//         MenuListProps={{ 'aria-labelledby': `long-button` }}
//         anchorEl={anchorEl}
//         open={open}
//         onClose={handleClose}
//         elevation={0}
//         anchorOrigin={{
//           vertical: 'bottom',
//           horizontal: 'right',
//         }}
//         transformOrigin={{
//           vertical: 'top',
//           horizontal: 'right',
//         }}
//       >
//         <MenuItem onClick={handleClick}>{item}</MenuItem>
//       </Menu>
//     </div>
//   );
// };

export const PositionCondition = ({
  direction,
  mode,
  conditions,
}: {
  direction: string;
  mode: string;
  conditions: FieldData[];
}) => {
  return (
    <Box className="m-main-spacing">
      <Box sx={{ py: 2 }}>
        <Typography variant="body3" fontWeight="500">
          <FormattedMessage id={`user_bot_startegy.${direction}`} />
        </Typography>
      </Box>
      <Box sx={{ mb: 2 }}>
        <StrategyBuilder
          inputConditions={conditions}
          isValid={true}
          mode={mode}
        />
      </Box>
    </Box>
  );
};

// ChartinkUrlBlock component for individual URLs
function ChartinkUrlBlock({
  userBotId,
  direction,
  authKey,
  type,
}: {
  userBotId: number | null | undefined;
  direction: 'long' | 'short';
  authKey: string;
  type: 'entry' | 'exit';
}) {
  const signalType = type === 'entry' ? '301' : '302';
  // Extract base URL from current window location
  const baseUrl = `${window.location.protocol}//${window.location.host}`;
  const url = `${baseUrl}/api/chartink/${
    userBotId ?? ''
  }/${direction}/${signalType}?key=${authKey}`;

  return (
    <Box
      sx={{
        mb: 2,
        mt: 1,
        bgcolor: '#e3f2fd', // Light blue background
        p: 2,
        borderRadius: 2,
        maxWidth: 900,
        width: '100%',
        ml: 6,
        mr: 6,
      }}
    >
      {/* Note section */}
      {/*
       */}

      {/* URL */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <InfoIcon sx={{ color: '#1976d2', mr: 1 }} />
        <Typography fontWeight={600} color="#1565c0">
          Chartink URL
        </Typography>
      </Box>
      <Box>
        <TextField
          fullWidth
          value={url}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => navigator.clipboard.writeText(url)}
                  sx={{ color: 'black' }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
            sx: {
              bgcolor: '#fff',
              borderRadius: 1,
              fontFamily: 'monospace',
              color: 'black',
            },
          }}
          variant="outlined"
          size="small"
        />
      </Box>
    </Box>
  );
}

const PositionDirectionConditions = ({
  direction,
  longEntryconditions,
  longExitconditions,
  shortEntryconditions,
  shortExitconditions,
  chartinkConfig,
}: {
  direction: string;
  longEntryconditions: FieldData[];
  longExitconditions: FieldData[];
  shortEntryconditions: FieldData[];
  shortExitconditions: FieldData[];
  chartinkConfig?: {
    userBotId: number | null | undefined;
    authKey: string;
    direction: string;
    showLong?: boolean;
    showShort?: boolean;
    showEntry?: boolean;
    showExit?: boolean;
  };
}) => {
  const [mode, setMode] = useState<string>(StrategyMode.VIEW);

  const handleChangeMode = () => {
    if (mode === StrategyMode.VIEW) setMode(StrategyMode.TEXT);
    else setMode(StrategyMode.VIEW);
  };

  const directionLabel = PositionDirectionData.filter(
    x => x.value === direction,
  )[0]?.label;
  return (
    <Box>
      <ItemHead
        variant="mediumWithIcon"
        title={
          <>
            <FormattedMessage id="user_bot_startegy.entry_exit" /> (
            {directionLabel})
          </>
        }
        icon={
          <IconButton
            onClick={handleChangeMode}
            size="small"
            sx={{
              backgroundColor: theme =>
                mode === StrategyMode.TEXT
                  ? 'transparent'
                  : theme.palette.grey[300] + ' !important',
              borderRadius: 1,
            }}
          >
            <CodeIcon />
          </IconButton>
        }
      />
      <Box className="m-main-spacing">
        <ItemSeparator variant="2px" />
      </Box>

      {/* Render ChartinkUrlBlock if chartinkConfig is present and enabled */}

      {(direction === 'long' || direction === 'both') && (
        <>
          <PositionCondition
            direction="long_entry"
            mode={mode}
            conditions={longEntryconditions}
          />
          <FeatureFlag
            features={[FEATURES.IS_CHARTINK_ENABLED]}
            fallback={<></>}
          >
            {chartinkConfig &&
              chartinkConfig.userBotId &&
              chartinkConfig.showEntry && (
                <ChartinkUrlBlock
                  userBotId={chartinkConfig.userBotId}
                  authKey={chartinkConfig.authKey}
                  direction="long"
                  type="entry"
                />
              )}
          </FeatureFlag>
        </>
      )}
      {(direction === 'long' || direction === 'both') && (
        <>
          <PositionCondition
            direction="long_exit"
            mode={mode}
            conditions={longExitconditions}
          />
          <FeatureFlag
            features={[FEATURES.IS_CHARTINK_ENABLED]}
            fallback={<></>}
          >
            {chartinkConfig &&
              chartinkConfig.userBotId &&
              chartinkConfig.showExit && (
                <ChartinkUrlBlock
                  userBotId={chartinkConfig.userBotId}
                  authKey={chartinkConfig.authKey}
                  direction="long"
                  type="exit"
                />
              )}
          </FeatureFlag>
        </>
      )}
      {(direction === 'short' || direction === 'both') && (
        <>
          <PositionCondition
            direction="short_entry"
            mode={mode}
            conditions={shortEntryconditions}
          />
          <FeatureFlag
            features={[FEATURES.IS_CHARTINK_ENABLED]}
            fallback={<></>}
          >
            {chartinkConfig &&
              chartinkConfig.userBotId &&
              chartinkConfig.showEntry && (
                <ChartinkUrlBlock
                  userBotId={chartinkConfig.userBotId}
                  authKey={chartinkConfig.authKey}
                  direction="short"
                  type="entry"
                />
              )}
          </FeatureFlag>
        </>
      )}
      {(direction === 'short' || direction === 'both') && (
        <>
          <PositionCondition
            direction="short_exit"
            mode={mode}
            conditions={shortExitconditions}
          />
          <FeatureFlag
            features={[FEATURES.IS_CHARTINK_ENABLED]}
            fallback={<></>}
          >
            {chartinkConfig &&
              chartinkConfig.userBotId &&
              chartinkConfig.showExit && (
                <ChartinkUrlBlock
                  userBotId={chartinkConfig.userBotId}
                  authKey={chartinkConfig.authKey}
                  direction="short"
                  type="exit"
                />
              )}
          </FeatureFlag>
        </>
      )}
    </Box>
  );
};

export default memo(PositionDirectionConditions);
